import React, { useEffect, useState } from 'react';
import { getRegistrationOptions, updateAccount, updatePreferences } from '../api';
import AchievementGrid from './AchievementGrid';
import { applyThemeWithAttribute, DEFAULT_THEME_COLOR } from '../theme';

function Account({ user, onUserChange, onBack, onLogout }) {
  const [options, setOptions] = useState({
    genders: [],
    consumptionStyles: [],
    strainTypes: [],
    themeColors: [],
  });
  const [account, setAccount] = useState({
    firstName: user.firstName || '',
    lastName: user.lastName || '',
    email: user.email || '',
    gender: user.gender || '',
    zipcode: user.zipcode || '',
    nickname: user.nickname || '',
    friendNameShare: user.friendNameShare === 'nickname' ? 'nickname' : 'full',
  });
  const [prefs, setPrefs] = useState({
    consumptionStyle: user.consumptionStyle || '',
    strainType: user.strainType || '',
    themeColor: user.themeColor || DEFAULT_THEME_COLOR,
    showMoods: user.showMoods !== false,
  });
  const [accountError, setAccountError] = useState('');
  const [accountMessage, setAccountMessage] = useState('');
  const [prefsError, setPrefsError] = useState('');
  const [prefsMessage, setPrefsMessage] = useState('');
  const [savingAccount, setSavingAccount] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    getRegistrationOptions()
      .then((data) => {
        setOptions({
          genders: data.genders || [],
          consumptionStyles: data.consumptionStyles || [],
          strainTypes: data.strainTypes || [],
          themeColors: data.themeColors || [],
        });
      })
      .catch(() => {
        setOptions({
          genders: ['Female', 'Male', 'Non-binary', 'Prefer not to say', 'Other'],
          consumptionStyles: [
            'Smoke',
            'Water Pipe (Bong)',
            'Vape',
            'Eat/Drink',
            'Topical',
            'Pill/Capsule',
            'Concentrates/Dab',
            'Heat not Burn',
          ],
          strainTypes: [
            'Sativa',
            'Indica',
            'Hybrid',
            'Indica leaning Hybrid',
            'Sativa leaning Hybrid',
          ],
        });
      });
  }, []);

  useEffect(() => {
    return () => {
      applyThemeWithAttribute(user.themeColor || DEFAULT_THEME_COLOR);
    };
  }, [user.themeColor]);

  const selectThemeColor = (themeColor) => {
    setPrefs((current) => ({ ...current, themeColor }));
    applyThemeWithAttribute(themeColor);
  };

  const friendNamePreview = (() => {
    if (account.friendNameShare === 'nickname' && account.nickname.trim()) {
      return account.nickname.trim();
    }
    const first = account.firstName.trim() || 'First';
    const last = account.lastName.trim();
    if (!last) return first;
    return `${first} ${last.charAt(0).toUpperCase()}.`;
  })();

  const saveAccount = async (e) => {
    e.preventDefault();
    setAccountError('');
    setAccountMessage('');
    setSavingAccount(true);
    try {
      const result = await updateAccount({
        userId: user.id,
        firstName: account.firstName.trim(),
        lastName: account.lastName.trim(),
        email: account.email.trim(),
        gender: account.gender,
        zipcode: account.zipcode.trim(),
        nickname: account.nickname.trim(),
        friendNameShare: account.friendNameShare,
      });
      onUserChange(result.user);
      setAccountMessage('Account details saved.');
    } catch (err) {
      setAccountError(err.error || 'Unable to save account details');
    } finally {
      setSavingAccount(false);
    }
  };

  const savePrefs = async (e) => {
    e.preventDefault();
    setPrefsError('');
    setPrefsMessage('');
    setSavingPrefs(true);
    try {
      const result = await updatePreferences({
        userId: user.id,
        consumptionStyle: prefs.consumptionStyle,
        strainType: prefs.strainType,
        themeColor: prefs.themeColor,
        showMoods: prefs.showMoods,
      });
      onUserChange(result.user);
      applyThemeWithAttribute(result.user.themeColor || DEFAULT_THEME_COLOR);
      setPrefs((current) => ({
        ...current,
        showMoods: result.user.showMoods !== false,
      }));
      setPrefsMessage('Preferences saved.');
    } catch (err) {
      setPrefsError(err.error || 'Unable to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  return (
    <div className="container wide">
      <p className="brand">My Weed Buddy</p>
      <h1>Account Details</h1>
      <p className="subtitle">Update your name, nickname, email, sex, and zip code.</p>

      {accountError && <div className="error">{accountError}</div>}
      {accountMessage && <div className="success">{accountMessage}</div>}

      <form onSubmit={saveAccount}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="firstName">First Name</label>
            <input
              id="firstName"
              value={account.firstName}
              onChange={(e) => setAccount((current) => ({ ...current, firstName: e.target.value }))}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lastName">Last Name</label>
            <input
              id="lastName"
              value={account.lastName}
              onChange={(e) => setAccount((current) => ({ ...current, lastName: e.target.value }))}
              required
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="nickname">
            Nickname <span className="optional">(optional)</span>
          </label>
          <input
            id="nickname"
            value={account.nickname}
            onChange={(e) => setAccount((current) => ({ ...current, nickname: e.target.value }))}
            maxLength={40}
            placeholder="What friends can call you"
          />
        </div>
        <fieldset className="form-group friend-name-share">
          <legend>Name shared with friends</legend>
          <p className="field-note">Choose what friends see in your list and feed.</p>
          <label className="radio-row">
            <input
              type="radio"
              name="friendNameShare"
              value="full"
              checked={account.friendNameShare === 'full'}
              onChange={() => setAccount((current) => ({ ...current, friendNameShare: 'full' }))}
            />
            First and last name
          </label>
          <label className="radio-row">
            <input
              type="radio"
              name="friendNameShare"
              value="nickname"
              checked={account.friendNameShare === 'nickname'}
              onChange={() => setAccount((current) => ({ ...current, friendNameShare: 'nickname' }))}
            />
            Nickname
          </label>
          <p className="field-note">Friends will see: <strong>{friendNamePreview}</strong></p>
        </fieldset>
        <div className="form-group">
          <label htmlFor="email">Email Address</label>
          <input
            type="email"
            id="email"
            value={account.email}
            onChange={(e) => setAccount((current) => ({ ...current, email: e.target.value }))}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor="gender">Sex</label>
          <select
            id="gender"
            value={account.gender}
            onChange={(e) => setAccount((current) => ({ ...current, gender: e.target.value }))}
          >
            <option value="">Select</option>
            {options.genders.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="zipcode">
            Zip code <span className="optional">(optional)</span>
          </label>
          <input
            id="zipcode"
            inputMode="numeric"
            autoComplete="postal-code"
            value={account.zipcode}
            onChange={(e) => setAccount((current) => ({ ...current, zipcode: e.target.value }))}
            placeholder="12345"
          />
        </div>
        <button type="submit" disabled={savingAccount}>
          {savingAccount ? 'Saving...' : 'Save account details'}
        </button>
      </form>

      <hr className="section-divider" />

      <h2>Preferences</h2>
      <p className="subtitle">Tell us how you like to consume and what type you prefer.</p>

      {prefsError && <div className="error">{prefsError}</div>}
      {prefsMessage && <div className="success">{prefsMessage}</div>}

      <form onSubmit={savePrefs}>
        <div className="form-group">
          <label htmlFor="consumptionStyle">Consumption style</label>
          <select
            id="consumptionStyle"
            value={prefs.consumptionStyle}
            onChange={(e) =>
              setPrefs((current) => ({ ...current, consumptionStyle: e.target.value }))
            }
          >
            <option value="">Select</option>
            {options.consumptionStyles.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label htmlFor="strainType">Type</label>
          <select
            id="strainType"
            value={prefs.strainType}
            onChange={(e) => setPrefs((current) => ({ ...current, strainType: e.target.value }))}
          >
            <option value="">Select</option>
            {options.strainTypes.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <span className="label-like">Theme color</span>
          <p className="field-note theme-note">Pick a color — buttons, backgrounds, and accents update app-wide.</p>
          <div className="theme-color-grid" role="radiogroup" aria-label="Theme color">
            {(options.themeColors.length
              ? options.themeColors
              : [{ id: 'green', label: 'Forest Green', swatch: '#2f7a4a' }]
            ).map((color) => (
              <button
                key={color.id}
                type="button"
                role="radio"
                aria-checked={prefs.themeColor === color.id}
                className={[
                  'theme-color-option',
                  prefs.themeColor === color.id ? 'selected' : '',
                ].join(' ')}
                onClick={() => selectThemeColor(color.id)}
                title={color.label}
              >
                <span className="theme-swatch" style={{ background: color.swatch }} aria-hidden="true" />
                <span className="theme-color-label">{color.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="form-group">
          <span className="label-like">Consumption form</span>
          <p className="field-note theme-note">
            Choose whether starting and ending moods appear when you log a session.
          </p>
          <button
            type="button"
            className={`pref-switch-row${prefs.showMoods ? ' on' : ''}`}
            role="switch"
            aria-checked={prefs.showMoods}
            onClick={() =>
              setPrefs((current) => ({ ...current, showMoods: !current.showMoods }))
            }
          >
            <span className="pref-switch-copy">
              <span className="pref-switch-title">Starting &amp; ending moods</span>
              <span className="pref-switch-status">
                {prefs.showMoods ? 'Shown on consumption form' : 'Hidden on consumption form'}
              </span>
            </span>
            <span className="pref-switch" aria-hidden="true">
              <span className="pref-switch-knob" />
            </span>
          </button>
        </div>
        <button type="submit" disabled={savingPrefs}>
          {savingPrefs ? 'Saving...' : 'Save preferences'}
        </button>
      </form>

      <hr className="section-divider" />

      <AchievementGrid userId={user.id} />

      <button type="button" className="secondary" onClick={onBack}>
        Back to welcome
      </button>
      <button type="button" className="secondary" onClick={onLogout}>
        Log out
      </button>
    </div>
  );
}

export default Account;
